/**
 * Reactive projection of the agency's commercial organisation.
 *
 * Centralises everything the role-aware UI needs: who am I (business role,
 * team), the data-sharing model, and the scope helpers ("Mes / Mon équipe /
 * Toute l'agence"). Every list screen + cockpit reads from here so the scoping
 * rules live in ONE place.
 *
 * Provided in root but safe outside an agency workspace: the Firestore streams
 * are gated on `workspace.type === 'agency'` (they emit empties elsewhere),
 * so injecting it from a non-agency context never hits the throwing
 * `AgencyService` path.
 */

import { Injectable, computed, inject } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AgencyReach } from '@core/navigation/agency-reach.token';
import { of, switchMap } from 'rxjs';
import { AgencyService } from './agency.service';
import { WorkspaceStore } from '../../../shared/services/workspace.store';
import { AuthStore } from '@core/services/auth/auth.store';
import { WorkspaceModel } from '../../../shared/definitions/workspace';
import {
  AgencyDataSharingModel,
  AgencyScope,
  AgencyTeam,
  AgencyTeamMember,
  DEFAULT_DATA_SHARING_MODEL,
  MANAGER_AGENCY_ROLES,
  availableScopes,
  canManageAgencyOrg,
  computeVisibleAgentIds,
  defaultScope,
  indexTeamMembersByUid,
  resolveAgencyRole,
} from '../../../shared/definitions/agency-team';

@Injectable({ providedIn: 'root' })
export class AgencyOrgService implements AgencyReach {
  private readonly agency = inject(AgencyService);
  private readonly workspaceStore = inject(WorkspaceStore);
  private readonly authStore = inject(AuthStore);

  private readonly wsType = computed(
    () => this.workspaceStore.currentWorkspace()?.type ?? null,
  );

  /** Business profiles of every member (empty outside an agency workspace). */
  readonly teamMembers = toSignal(
    toObservable(this.wsType).pipe(
      switchMap((t) =>
        t === 'agency' ? this.agency.teamMembers$() : of([] as AgencyTeamMember[]),
      ),
    ),
    { initialValue: [] as AgencyTeamMember[] },
  );

  /** Teams / branches of the agency. */
  readonly teams = toSignal(
    toObservable(this.wsType).pipe(
      switchMap((t) => (t === 'agency' ? this.agency.teams$() : of([] as AgencyTeam[]))),
    ),
    { initialValue: [] as AgencyTeam[] },
  );

  private readonly profile = toSignal(
    toObservable(this.wsType).pipe(
      switchMap((t) => (t === 'agency' ? this.agency.profile$() : of(undefined))),
    ),
  );

  readonly myUid = computed(() => this.authStore.user()?.userUID ?? '');

  private readonly wsModel = computed(
    () => this.workspaceStore.currentWorkspace() as WorkspaceModel | null,
  );
  readonly ownerId = computed(() => this.wsModel()?.ownerId ?? '');

  private readonly docsByUid = computed(() => indexTeamMembersByUid(this.teamMembers()));
  readonly myMember = computed<AgencyTeamMember | undefined>(
    () => this.docsByUid()[this.myUid()],
  );

  readonly myRole = computed(() =>
    resolveAgencyRole(this.myUid(), this.ownerId(), this.myMember()),
  );
  readonly myTeamId = computed(() => this.myMember()?.teamId);

  readonly dataSharingModel = computed<AgencyDataSharingModel>(
    () => this.profile()?.dataSharingModel ?? DEFAULT_DATA_SHARING_MODEL,
  );

  private readonly myTechnicalRole = computed(
    () => this.wsModel()?.members?.[this.myUid()]?.role ?? null,
  );

  /** Owner/admin (technical) OR business principal → sees the whole agency. */
  readonly fullReach = computed(
    () => canManageAgencyOrg(this.myTechnicalRole()) || this.myRole() === 'principal',
  );
  /** Business manager (and not full-reach) → sees their team. */
  readonly teamReach = computed(
    () => !this.fullReach() && MANAGER_AGENCY_ROLES.includes(this.myRole()),
  );

  /** Owner/admin only — may administer the org (assign roles, teams, sharing). */
  readonly canManageOrg = computed(() => canManageAgencyOrg(this.myTechnicalRole()));

  // -------------------------------------------------------------------------
  // Scope helpers (consumed by the scope selector + every list screen)
  // -------------------------------------------------------------------------

  /** Allowed scopes for a screen (`inventory` = properties/mandates). */
  scopesFor(inventory: boolean): AgencyScope[] {
    return availableScopes(
      this.fullReach(),
      this.teamReach(),
      this.dataSharingModel(),
      inventory,
    );
  }

  /** The scope a screen opens on. */
  defaultScopeFor(inventory: boolean): AgencyScope {
    return defaultScope(this.fullReach(), this.teamReach(), inventory);
  }

  /** Visible agent uids for a scope, or null for "everyone". */
  agentIdsForScope(scope: AgencyScope): Set<string> | null {
    return computeVisibleAgentIds(
      scope,
      this.myUid(),
      this.myTeamId(),
      this.teamMembers(),
      this.teams(),
    );
  }

  /** Resolve a member's display name from the workspace members map. */
  displayName(uid: string): string {
    const m = this.wsModel()?.members?.[uid];
    return m?.displayName || m?.email || uid;
  }

  /** All workspace member uids (used by the manager performance board). */
  readonly memberUids = computed(() => Object.keys(this.wsModel()?.members ?? {}));
}
